import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Card,
  Spin,
  Alert,
  Typography,
  Space,
  Button,
  Tabs,
} from "antd";
import {
  getVendorAgingReport,
  exportVendorAgingReport,
} from "../../api/accounting";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import ExcelExportModal from "../Shared/Modals/ExcelExportModal";
import UpcomingInvoices from "./UpcomingInvoices";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";

const { Title } = Typography;

const AgingReport = ({ filters = {}, selectedBrand = null }) => {
  const [loading, setLoading] = useState(false);
  const [agingData, setAgingData] = useState({
    vendors: [],
    bucket_totals: {},
    grand_total: 0,
  });
  const [error, setError] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const getValueColor = (value) => (value < 0 ? "green" : "inherit");

  const { selectedBrands, brandIds } = useGlobalFilters();

  const fetchAgingReport = useCallback(async () => {
    if (brandIds.length === 0) {
      setError("Please select a brand from the header");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiFilters = {};
      if (filters.dateRange && filters.dateRange.length === 2) {
        if (filters.dateRange[0]) {
          apiFilters.start_date = filters.dateRange[0].format("YYYY-MM-DD");
        }
        if (filters.dateRange[1]) {
          apiFilters.end_date = filters.dateRange[1].format("YYYY-MM-DD");
        }
      }
      if (filters.partnerType && filters.partnerType !== "all") {
        apiFilters.partner_type = filters.partnerType;
      }
      if (filters.status && filters.status !== "all") {
        apiFilters.status = filters.status;
      }

      const data = await getVendorAgingReport(brandIds, apiFilters);
      setAgingData(data);
    } catch (err) {
      console.error("Error fetching vendor aging report:", err);
      setError("Failed to load vendor aging report. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [brandIds, filters]);

  const formatCurrency = (value) => {
    const formatted = Math.abs(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return value < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const columnHeaders = {
    upcoming_90_60: "Upcoming 90-60 Days",
    upcoming_60_30: "Upcoming 60-30 Days",
    upcoming_30_1: "Upcoming 30-1 Days",
    current: "Current",
    past_1_30: "Past 1-30 Days",
    past_30_60: "Past 30-60 Days",
    past_60_90: "Past 60-90 Days",
    past_90_plus: "Past 90+ Days",
  };

  const columns = [
    {
      title: "Vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      fixed: "left",
      sorter: (a, b) => a.vendor_name.localeCompare(b.vendor_name),
    },
    {
      title: "Overpayment",
      dataIndex: "overpayment_amount",
      key: "overpayment_amount",
      align: "right",
      sorter: (a, b) => a.overpayment_amount - b.overpayment_amount,
      render: (value) => formatCurrency(value || 0),
    },
    ...Object.entries(columnHeaders).map(([key, title]) => ({
      title,
      dataIndex: key,
      key,
      align: "right",
      sorter: (a, b) => a[key] - b[key],
      render: (value) => formatCurrency(value || 0),
    })),
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      fixed: "right",
      align: "right",
      sorter: (a, b) => a.total - b.total,
      // render: (value) => <strong>{formatCurrency(value || 0)}</strong>,
      render: (value) => (
        <strong style={{ color: getValueColor(value) }}>
          {formatCurrency(value || 0)}
        </strong>
      ),
    },
  ];

  const handleExportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExportModalVisible(true);
  };

  const handleExport = async (returnFile = false) => {
    if (brandIds.length === 0) {
      throw new Error("Please select a brand from the header");
    }

    setExportLoading(true);
    try {
      const apiFilters = {};
      if (filters.dateRange && filters.dateRange.length === 2) {
        if (filters.dateRange[0]) {
          apiFilters.start_date = filters.dateRange[0].format("YYYY-MM-DD");
        }
        if (filters.dateRange[1]) {
          apiFilters.end_date = filters.dateRange[1].format("YYYY-MM-DD");
        }
      }
      if (filters.partnerType && filters.partnerType !== "all") {
        apiFilters.partner_type = filters.partnerType;
      }
      if (filters.status && filters.status !== "all") {
        apiFilters.status = filters.status;
      }

      const response = await exportVendorAgingReport(brandIds, apiFilters);

      if (returnFile) {
        return response.data;
      } else {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "vendor_aging_report.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export error:", error);
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (brandIds.length > 0) {
      fetchAgingReport();
    }
  }, [brandIds, fetchAgingReport]);

  return (
    <Card className="aging-report-card">
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Tabs
          defaultActiveKey="aging"
          items={[
            {
              key: "aging",
              label: "Aging Report",
              children: (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginRight: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <Title level={4} style={{ margin: 0 }}>
                      Vendor Invoice Aging Report
                    </Title>
                    <Button
                      onClick={handleExportClick}
                      icon={
                        <ImageWithBasePath
                          src="assets/img/icons/xls.svg"
                          alt="Excel"
                          style={{ width: 16, height: 16 }}
                        />
                      }
                      title="Export Aging Report to Excel"
                      disabled={brandIds.length === 0}
                    />
                  </div>

                  {error && <Alert message={error} type="error" showIcon />}

                  <Spin spinning={loading}>
                    <Table
                      dataSource={agingData.vendors}
                      columns={columns}
                      rowKey="vendor_id"
                      pagination={false}
                      bordered
                      scroll={{ y: 500 }}
                      summary={() => {
                        if (agingData.vendors.length === 0) return null;

                        const totalOverpayment = agingData.vendors.reduce(
                          (sum, v) => sum + (v.overpayment_amount || 0),
                          0
                        );

                        return (
                          <Table.Summary fixed>
                            <Table.Summary.Row>
                              <Table.Summary.Cell index={0}>
                                <strong>Total</strong>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={1} align="right">
                                <strong>
                                  {formatCurrency(totalOverpayment)}
                                </strong>
                              </Table.Summary.Cell>
                              {Object.keys(columnHeaders).map((key, index) => (
                                <Table.Summary.Cell
                                  key={key}
                                  index={index + 2}
                                  align="right"
                                >
                                  <strong>
                                    {formatCurrency(
                                      agingData.bucket_totals[key] || 0
                                    )}
                                  </strong>
                                </Table.Summary.Cell>
                              ))}
                              <Table.Summary.Cell
                                index={Object.keys(columnHeaders).length + 2}
                                align="right"
                              >
                                <strong
                                  style={{
                                    color:
                                      agingData.grand_total < 0
                                        ? "green"
                                        : agingData.grand_total > 0
                                        ? "red"
                                        : "inherit",
                                  }}
                                >
                                  {formatCurrency(agingData.grand_total || 0)}
                                </strong>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          </Table.Summary>
                        );
                      }}
                    />
                  </Spin>
                </>
              ),
            },
            {
              key: "upcoming",
              label: "Upcoming Invoices",
              children: (
                <UpcomingInvoices
                  selectedBrand={
                    selectedBrands.length > 0 ? selectedBrands[0] : null
                  }
                />
              ),
            },
          ]}
        />
      </Space>

      <ExcelExportModal
        visible={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onExport={handleExport}
        exportType="Vendor Aging Report"
        loading={exportLoading}
      />
    </Card>
  );
};

export default AgingReport;
